import appConfig from "constants/appConfig";
import useSWRImmutable from "swr/immutable";
import { fetchFbToken } from "utility/firebase";
import * as z from "zod/mini";

type AddressData = {
  zipCode: string;
  prefecture: string;
  city: string;
  streetNum: string;
  buildingInfo: string;
  recipientName: string;
};

const AddressResponseSchema = z.object({
  userAddress: z.object({
    zipCode: z.string(),
    prefecture: z.string(),
    city: z.string(),
    streetNum: z.string(),
    buildingInfo: z.string(),
    recipientName: z.string(),
  }),
});

type UseOrderAddressInput = {
  /** SPWN側の配送先ID。配送先のない注文はnull */
  shippingUniqueId: string | null;
};

type UseOrderAddressOutput = {
  addressData: AddressData | null;
  isLoading: boolean;
};

const fetchAddressData = async (
  shippingUniqueId: string
): Promise<AddressData | null> => {
  const fbToken = await fetchFbToken();
  if (!fbToken) {
    console.error("Failed to fetch Firebase token");
    return null;
  }

  const url = appConfig.CloudFunctions.loadAddressInfo;
  if (!url) {
    console.error("Missing cloud function URL for loading address info");
    return null;
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${fbToken}`,
    },
    body: JSON.stringify({ addressIdx: shippingUniqueId }),
  });

  if (response.status !== 200) {
    console.error(`Failed to fetch address data: ${response.statusText}`);
    return null;
  }

  const parseResult = AddressResponseSchema.safeParse(await response.json());
  if (!parseResult.success) {
    console.error("Invalid address response:", parseResult.error);
    return null;
  }
  const { userAddress } = parseResult.data;

  return {
    zipCode: userAddress.zipCode,
    prefecture: userAddress.prefecture,
    city: userAddress.city,
    streetNum: userAddress.streetNum,
    buildingInfo: userAddress.buildingInfo,
    recipientName: userAddress.recipientName,
  };
};

export const useOrderAddress = (
  input: UseOrderAddressInput
): UseOrderAddressOutput => {
  const { shippingUniqueId: shippingId } = input;
  const hasShippingId = shippingId !== null;

  const { data: addressData = null, isLoading } = useSWRImmutable(
    hasShippingId ? ["orderAddress", shippingId] : null,
    hasShippingId ? async () => fetchAddressData(shippingId) : null
  );

  return {
    addressData,
    isLoading,
  };
};
